import React, { useEffect, useState } from "react";
import { CheckCircle2, Link2, Loader2, Save, Shield } from "lucide-react";
import { toast } from "sonner";
import { epostaMerkez } from "@/lib/api";

const STEPS = [
    { id: 1, title: "Azure Client ID", desc: "Uygulama (istemci) ve kiracı kimliğini kaydedin." },
    { id: 2, title: "Yönetici onayı", desc: "IMAP izni için Microsoft yönetici onayı (bir kez)." },
    { id: 3, title: "Hotmail bağla", desc: "Device code ile hesabı bağlayın ve tamamlayın." },
];

export default function EpostaOAuthWizard({ onConnected }) {
    const [step, setStep] = useState(1);
    const [azureClientInput, setAzureClientInput] = useState("");
    const [tenantInput, setTenantInput] = useState("");
    const [azureConfigured, setAzureConfigured] = useState(false);
    const [azureSaveBusy, setAzureSaveBusy] = useState(false);
    const [oauthBusy, setOauthBusy] = useState(false);
    const [oauthCode, setOauthCode] = useState(null);
    const [oauthReady, setOauthReady] = useState(false);

    useEffect(() => {
        epostaMerkez
            .azureClientGet()
            .then((d) => {
                setAzureConfigured(!!d.configured);
                setOauthReady(!!d.graph_oauth_ready);
                if (d.client_id) setAzureClientInput(d.client_id);
                if (d.tenant_id) setTenantInput(d.tenant_id);
                if (d.graph_oauth_ready) setStep(3);
            })
            .catch(() => {});
        epostaMerkez.ozet().then((o) => setOauthReady(!!o.graph_oauth_ready)).catch(() => {});
    }, []);

    async function handleSaveAzure(e) {
        e?.preventDefault?.();
        const cid = azureClientInput.trim();
        if (!cid) {
            toast.error("Client ID girin.");
            return;
        }
        setAzureSaveBusy(true);
        try {
            await epostaMerkez.azureClientSet(cid, tenantInput.trim());
            setAzureConfigured(true);
            toast.success("Azure kaydedildi. Adım 2'ye geçin.");
            setStep(2);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Kaydedilemedi.");
        } finally {
            setAzureSaveBusy(false);
        }
    }

    async function handleHotmailBagla() {
        if (!azureConfigured) {
            toast.error("Önce Client ID kaydedin.");
            setStep(1);
            return;
        }
        setOauthBusy(true);
        setOauthCode(null);
        try {
            const start = await epostaMerkez.oauthBaslat();
            if (start.already_connected || start.graph_oauth_ready) {
                toast.success("Hotmail zaten bağlı.");
                setOauthReady(true);
                onConnected?.();
                return;
            }
            setOauthCode({
                user_code: start.user_code,
                uri: start.verification_uri || "https://login.microsoft.com/device",
            });
            setStep(3);
            window.open(start.verification_uri || "https://login.microsoft.com/device", "_blank", "noopener");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Kod alınamadı.");
        } finally {
            setOauthBusy(false);
        }
    }

    async function handleOauthTamamla() {
        setOauthBusy(true);
        try {
            const done = await epostaMerkez.oauthTamamla();
            if (done.ok) {
                toast.success("Hotmail bağlandı.");
                setOauthReady(true);
                setOauthCode(null);
                onConnected?.();
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Bağlantı tamamlanamadı.");
        } finally {
            setOauthBusy(false);
        }
    }

    async function handleSifirla() {
        if (!window.confirm("Hotmail bağlantısı sıfırlansın mı?")) return;
        try {
            await epostaMerkez.sifirla();
            setAzureConfigured(false);
            setOauthReady(false);
            setStep(1);
            toast.success("Sıfırlandı.");
        } catch {
            toast.error("Sıfırlanamadı.");
        }
    }

    return (
        <section className="rounded-xl border border-sky-500/20 bg-[#0B0F19]/80 p-4 space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-sky-200 flex items-center gap-2">
                    <Shield className="h-4 w-4" /> Hotmail bağlantı sihirbazı
                </h3>
                {oauthReady && (
                    <span className="text-xs text-emerald-400 flex items-center gap-1">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Bağlı
                    </span>
                )}
            </div>

            <div className="flex gap-2">
                {STEPS.map((s) => (
                    <button
                        key={s.id}
                        type="button"
                        onClick={() => setStep(s.id)}
                        className={`flex-1 rounded-lg border px-2 py-2 text-left transition ${
                            step === s.id
                                ? "border-sky-500/40 bg-sky-500/10"
                                : "border-white/10 bg-black/20"
                        }`}
                    >
                        <div className="text-[10px] text-slate-500">Adım {s.id}</div>
                        <div className="text-xs font-medium text-slate-200">{s.title}</div>
                    </button>
                ))}
            </div>

            {step === 1 && (
                <form onSubmit={handleSaveAzure} className="space-y-2">
                    <p className="text-xs text-slate-400">{STEPS[0].desc}</p>
                    <input
                        value={azureClientInput}
                        onChange={(e) => setAzureClientInput(e.target.value)}
                        placeholder="Uygulama (istemci) kimliği"
                        className="w-full h-9 rounded border border-white/10 bg-black/30 px-3 text-xs font-mono"
                    />
                    <input
                        value={tenantInput}
                        onChange={(e) => setTenantInput(e.target.value)}
                        placeholder="Kiracı kimliği (isteğe bağlı)"
                        className="w-full h-9 rounded border border-white/10 bg-black/30 px-3 text-xs font-mono"
                    />
                    <button
                        type="submit"
                        disabled={azureSaveBusy}
                        className="h-9 px-4 rounded bg-amber-600 text-white text-xs font-semibold inline-flex items-center gap-1"
                    >
                        {azureSaveBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                        Kaydet
                    </button>
                </form>
            )}

            {step === 2 && (
                <div className="space-y-3">
                    <p className="text-xs text-slate-400">{STEPS[1].desc}</p>
                    <a
                        href="https://login.microsoftonline.com/67fc1eeb-e241-458c-abee-aeb1171ff338/adminconsent?client_id=7622db87-480e-4020-8b4a-3e37ef4abad0"
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center justify-center h-10 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-xs font-semibold"
                    >
                        Microsoft yönetici onayı — tıklayın
                    </a>
                    <button
                        type="button"
                        onClick={() => setStep(3)}
                        className="h-9 px-4 rounded border border-white/10 text-xs text-slate-300"
                    >
                        Onayı verdim → Adım 3
                    </button>
                </div>
            )}

            {step === 3 && (
                <div className="space-y-3">
                    <p className="text-xs text-slate-400">{STEPS[2].desc}</p>
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={handleHotmailBagla}
                            disabled={oauthBusy || !azureConfigured}
                            className="h-9 px-4 rounded bg-sky-600 text-white text-xs font-semibold disabled:opacity-50"
                        >
                            Hotmail bağla (kod al)
                        </button>
                        <button type="button" onClick={handleSifirla} className="h-9 px-3 rounded border border-white/10 text-xs text-slate-400">
                            Sıfırla
                        </button>
                    </div>
                    {oauthCode && (
                        <div className="rounded-lg border border-sky-500/40 bg-sky-950/40 p-4 space-y-3">
                            <div className="text-center font-mono text-2xl tracking-widest text-[#F3E5AB]">
                                {oauthCode.user_code}
                            </div>
                            <a
                                href={oauthCode.uri}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center justify-center gap-2 h-10 rounded-lg bg-sky-600 text-white text-sm font-semibold"
                            >
                                <Link2 className="h-4 w-4" /> Microsoft sayfasını aç
                            </a>
                            <button
                                type="button"
                                onClick={handleOauthTamamla}
                                disabled={oauthBusy}
                                className="w-full h-10 rounded-lg bg-emerald-600 text-white text-sm font-semibold inline-flex items-center justify-center gap-2"
                            >
                                {oauthBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                                Bağlantıyı tamamla
                            </button>
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}
